import { FormEvent, useEffect, useMemo, useRef, useState } from 'react';
import * as userTeamsApi from '../api/userTeams';

interface Props {
  open: boolean;
  onClose: () => void;
  onDone: (toastMessage: string) => Promise<void> | void;
}

function slugify(name: string): string {
  return name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

function randomTeamColor(): string {
  const swatches = ['#3b82f6', '#8b5cf6', '#10b981', '#f59e0b', '#ec4899', '#6366f1', '#14b8a6'];
  return swatches[Math.floor(Math.random() * swatches.length)];
}

/**
 * Create a new user-team — a named grouping of users used only for the
 * ViewerToggle filter. Distinct from a Company (access-group partition):
 * creating a user-team changes nothing about who can see which tasks.
 */
export function CreateUserTeamModal({ open, onClose, onDone }: Props) {
  const [displayName, setDisplayName] = useState('');
  const [id, setId] = useState('');
  const [color, setColor] = useState('#3b82f6');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const nameRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!open) return;
    setDisplayName('');
    setId('');
    setColor(randomTeamColor());
    setErr(null);
    setTimeout(() => nameRef.current?.focus(), 0);
  }, [open]);

  const autoId = useMemo(() => slugify(displayName.trim()), [displayName]);
  const effectiveId = id.trim() || autoId;
  const canSubmit = displayName.trim().length > 0 && effectiveId.length > 0 && !busy;

  if (!open) return null;

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (!canSubmit) return;
    setBusy(true);
    setErr(null);
    try {
      await userTeamsApi.createUserTeam({
        displayName: displayName.trim(),
        color,
        id: id.trim() || undefined,
      });
      await onDone(`Created ${displayName.trim()}`);
    } catch (ex) {
      setErr((ex as Error).message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div
      className="new-task-overlay"
      role="dialog"
      aria-modal="true"
      aria-label="Create team"
      onClick={onClose}
      onKeyDown={(e) => {
        if (e.key === 'Escape') onClose();
      }}
    >
      <form
        className="new-task-dialog settings-modal"
        onClick={(e) => e.stopPropagation()}
        onSubmit={submit}
      >
        <header>
          <h3>New team</h3>
          <button type="button" onClick={onClose} aria-label="Close">×</button>
        </header>

        <p className="muted small">
          Teams group users for filtering the task list. They do not control
          who can see which tasks — that's handled by Companies.
        </p>

        <label>
          <span>Display name</span>
          <input
            ref={nameRef}
            type="text"
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            placeholder="MJK Universal"
            required
          />
        </label>

        <div className="new-task-grid">
          <label>
            <span>ID (slug)</span>
            <input
              type="text"
              value={id}
              onChange={(e) => setId(e.target.value)}
              placeholder={autoId || 'auto-generated'}
            />
          </label>
          <label>
            <span>Color</span>
            <input
              type="color"
              value={color}
              onChange={(e) => setColor(e.target.value)}
            />
          </label>
        </div>

        {err && <div className="settings-error">{err}</div>}

        <div className="new-task-footer">
          <span className="muted small">
            Will be created as <code>{effectiveId || 'auto'}</code>.
          </span>
          <button type="submit" className="primary" disabled={!canSubmit}>
            {busy ? 'Creating…' : 'Create team'}
          </button>
        </div>
      </form>
    </div>
  );
}
